import * as React from 'react';
import { useState } from 'react';
import { createFileRoute, notFound } from '@tanstack/react-router';
import { showComponentLibrary } from '@/components/layout/sidebar';

import { CitationChip, type CitationChipData } from '@/components/ui/citation-chip';
import { CitationSourceModal } from '@/components/modals/citation-source-modal';
import { PostureChip, type PostureStatus } from '@/components/ui/posture-chip';
import { AiConfidenceChip } from '@/components/ui/ai-confidence-chip';
import { AiSurface } from '@/components/ui/ai-surface';
import { ImageSlot } from '@/components/ui/image-slot';
import { WorkspaceShowcase } from '@/components/ui/workspace/workspace-showcase';
import { useSnapshotMode, type SnapshotKind } from '@/lib/stores/snapshot-mode';

/**
 * E10 — Internal component library reference (`#components` in the
 * prototype). Not a user-facing screen; engineers open this to verify
 * load-bearing primitives render identically to the prototype before
 * dropping them into product surfaces.
 *
 * Each T-009X lane owns one section here:
 *   - T-0092: Posture chip + AI confidence chip
 *   - T-0093: Citation chip + source modal
 *   - T-0094: KPI source pill + verb chip + provenance chip
 *   - T-0095: AI surface + snapshot banner + image-slot
 *   - T-0140: Workspace patterns (layout, tree rail, virtualised grid,
 *             side panel, sheet, bulk bar, command palette, filter chips,
 *             toast) — examples live in ui/workspace/workspace-showcase.tsx
 *
 * Resolve merge conflicts via interleave: keep every section.
 *
 * Listed in the sidebar, and reachable at all, in development builds only.
 */
export const Route = createFileRoute('/components')({
  // Development builds only (C2-40): in production the address is a 404
  // like any other unknown page, as the sidebar no longer lists it.
  beforeLoad: () => {
    if (!showComponentLibrary()) throw notFound();
  },
  component: ComponentsShowcase,
});

const POSTURE_STATUSES: PostureStatus[] = ['healthy', 'at-risk', 'critical', 'unassessed'];

// Representative confidence values spanning the three bands defined in
// README §6 (high >= 0.85, medium 0.65..0.84, low < 0.65). The 0.84 / 0.65
// neighbors prove the boundary handling.
const AI_CONFIDENCE_VALUES = [0.97, 0.85, 0.84, 0.72, 0.65, 0.64, 0.42, 0.0];

const DEMO_CITES: CitationChipData[] = [
  {
    id: 'cite-demo-1',
    doc_label: 'SOW v2',
    section: '4.3.2',
    page: 18,
    confidence: 0.91,
    status: 'ok',
  },
  {
    id: 'cite-demo-2',
    doc_label: 'Status Report — 05-07',
    section: '3',
    page: 4,
    confidence: 0.78,
    status: 'ok',
  },
  {
    id: 'cite-demo-3',
    doc_label: 'IV&V Letter — Runbook v0.4',
    section: '2',
    page: 3,
    confidence: 0.62,
    status: 'ok',
  },
  {
    id: 'cite-demo-4',
    doc_label: 'CO #3 Draft (deleted)',
    section: '2.1',
    page: 2,
    confidence: 0.74,
    status: 'broken',
  },
  {
    id: 'cite-demo-5',
    doc_label: 'P-Card Interface Contract.xlsx',
    cell: 'B12',
    confidence: 0.86,
    status: 'ok',
  },
];

// T-0095 — snapshot banner kind toggles. Each option drives the showcase's
// `enterSnapshotMode` button so engineers can see the banner copy by kind.
const SNAPSHOT_KIND_OPTIONS: { kind: SnapshotKind; label: string }[] = [
  { kind: 'manual', label: 'Manual capture' },
  { kind: 'pulse', label: 'Pulse snapshot' },
  { kind: 'readiness', label: 'Readiness snapshot' },
  { kind: 'health', label: 'Health snapshot' },
  { kind: 'client_viewer', label: 'Client viewer' },
];

// T-0095 — image-slot showcase uses a sentinel UUID so a click on the
// picker in dev doesn't hit a real endpoint with a real engagement id.
// Real consumers (T-0097 hero) pass the route param.
const FAKE_ENGAGEMENT_ID = '00000000-0000-0000-0000-000000000000';

function ComponentsShowcase() {
  const [openCite, setOpenCite] = useState<CitationChipData | null>(null);

  return (
    <div className="space-y-10">
      <header>
        <h1 className="text-h1 font-semibold text-ink-900">Component library</h1>
        <p className="mt-1 text-body text-ink-500">
          Load-bearing UI primitives. Click any citation chip to open the source modal.
        </p>
      </header>

      {/* T-0092 — Posture chip */}
      <Section
        title="Posture chip"
        description={
          '<PostureChip status="healthy | at-risk | critical | unassessed" dot? /> — 22px pill, tinted bg + tinted-darker ink, optional dot prefix.'
        }
      >
        <ShowcaseRow label="No dot">
          {POSTURE_STATUSES.map((status) => (
            <PostureChip key={status} status={status} />
          ))}
        </ShowcaseRow>

        <ShowcaseRow label="With dot">
          {POSTURE_STATUSES.map((status) => (
            <PostureChip key={`${status}-dot`} status={status} dot />
          ))}
        </ShowcaseRow>

        <ShowcaseRow label="Custom label">
          <PostureChip status="healthy" dot>
            On track
          </PostureChip>
          <PostureChip status="at-risk" dot>
            Watch
          </PostureChip>
          <PostureChip status="critical" dot>
            Escalate
          </PostureChip>
        </ShowcaseRow>
      </Section>

      {/* T-0092 — AI confidence chip */}
      <Section
        title="AI confidence chip"
        description={
          '<AiConfidenceChip value={0..1} /> — AI-surface bg, JetBrains Mono, 2-decimal display. Bands: ≥0.85 high, 0.65–0.84 medium, <0.65 low.'
        }
      >
        <ShowcaseRow label="Value sweep">
          {AI_CONFIDENCE_VALUES.map((v) => (
            <AiConfidenceChip key={v} value={v} />
          ))}
        </ShowcaseRow>

        <ShowcaseRow label="Boundary cases">
          <AiConfidenceChip value={0.85} />
          <span className="text-mono-sm font-mono text-ink-300">↑ high cutoff</span>
          <AiConfidenceChip value={0.65} />
          <span className="text-mono-sm font-mono text-ink-300">↑ medium cutoff</span>
        </ShowcaseRow>
      </Section>

      {/* T-0093 — Citation chip + source modal */}
      <Section
        title="Citation chip"
        description="Soft blue-grey pill with mono locator + mono confidence. Click opens the source modal. Broken citations strike through in red."
      >
        <div className="flex flex-wrap items-center gap-2">
          {DEMO_CITES.map((c) => (
            <CitationChip key={c.id} cite={c} onOpen={() => setOpenCite(c)} />
          ))}
        </div>
      </Section>

      {/* T-0095 — AI surface */}
      <Section
        title="AI surface"
        description='<AiSurface as="div|tr" /> — lavender background + ink-purple text marking AI-derived content. Polymorphic so the same primitive wraps a synthesis card, a chat bubble, OR a register row.'
      >
        <ShowcaseRow label="As div (card)">
          <AiSurface className="max-w-md rounded-card p-4">
            <div className="text-h3 font-semibold">AI exec summary draft</div>
            <p className="mt-1 text-body-sm">
              Vendor delivery is trending behind schedule on two milestones; six interview findings
              corroborate the slip. Recommend revisiting the change-order timeline.
            </p>
          </AiSurface>
        </ShowcaseRow>

        <ShowcaseRow label="As tr (table row)">
          <table className="w-full max-w-2xl border border-ink-100 bg-paper-0">
            <thead>
              <tr className="bg-paper-100 text-meta text-ink-500">
                <th className="px-3 py-2 text-left font-medium">Risk</th>
                <th className="px-3 py-2 text-left font-medium">Severity</th>
                <th className="px-3 py-2 text-left font-medium">Origin</th>
              </tr>
            </thead>
            <tbody>
              <tr className="text-body-sm">
                <td className="px-3 py-2">UAT defect backlog growth rate</td>
                <td className="px-3 py-2">High</td>
                <td className="px-3 py-2">Consultant-edited</td>
              </tr>
              <AiSurface as="tr" className="text-body-sm">
                <td className="px-3 py-2">Vendor data-migration cutover risk</td>
                <td className="px-3 py-2">High</td>
                <td className="px-3 py-2">AI-proposed</td>
              </AiSurface>
              <tr className="text-body-sm">
                <td className="px-3 py-2">Training rollout schedule</td>
                <td className="px-3 py-2">Medium</td>
                <td className="px-3 py-2">Manual</td>
              </tr>
            </tbody>
          </table>
        </ShowcaseRow>
      </Section>

      {/* T-0095 — Snapshot banner */}
      <SnapshotBannerShowcase />

      {/* T-0095 — Image slot */}
      <Section
        title="Image slot"
        description="<ImageSlot engagementId projectId? defaultImageUrl? /> — drag-drop hero target. Gradient overlay baked in so the title stays legible whether or not a photo is set. Drops upload via the T-0040 chunked path then attach via the T-0091 banner endpoint."
      >
        <ShowcaseRow label="Empty state">
          <ImageSlot engagementId={FAKE_ENGAGEMENT_ID} className="h-[200px] w-[480px]" />
        </ShowcaseRow>

        <ShowcaseRow label="With hero overlay">
          <ImageSlot engagementId={FAKE_ENGAGEMENT_ID} className="h-[280px] w-[640px]">
            <div className="flex items-start justify-between gap-3">
              <span className="rounded-chip border border-ink-200 bg-paper-0/85 px-2 py-0.5 text-meta text-ink-700">
                City of Brightwater
              </span>
              <span className="rounded-chip bg-posture-at-risk-tint px-2 py-0.5 text-meta font-medium text-posture-at-risk">
                At risk
              </span>
            </div>
            <div className="mt-auto">
              <div className="font-serif text-3xl font-semibold text-paper-0">
                ERP Modernization — Phase 2
              </div>
              <div className="mt-1 text-body-sm text-paper-0/90">
                UAT · vendor Cascadia Public Systems
              </div>
            </div>
          </ImageSlot>
        </ShowcaseRow>

        <ShowcaseRow label="Pre-filled (default URL)">
          <ImageSlot
            engagementId={FAKE_ENGAGEMENT_ID}
            defaultImageUrl="https://images.unsplash.com/photo-1480714378408-67cf0d13bc1b?auto=format&fit=crop&w=1200&q=80"
            className="h-[200px] w-[480px]"
          />
        </ShowcaseRow>
      </Section>

      {/* T-0140 — Workspace patterns (shared components for E11 screens) */}
      <WorkspaceShowcase />

      {/* The modal is mounted once at the route level; chip clicks set
          the citation it should render. Demo uses a non-existent
          engagement id so the API returns an error and the modal shows
          its empty-state fallback — until the backend resolver lands
          that's the expected v1 behavior. */}
      {openCite ? (
        <CitationSourceModal
          engagementId="00000000-0000-0000-0000-000000000000"
          cite={openCite}
          open={openCite != null}
          onOpenChange={(open) => {
            if (!open) setOpenCite(null);
          }}
        />
      ) : null}
    </div>
  );
}

/**
 * Snapshot-banner showcase block — lives in its own component because
 * it owns the shared store side-effects (entering / exiting the
 * showcase fixture) and needs the standard Section + ShowcaseRow
 * helpers, which is cleaner than threading store hooks through the
 * top-level component.
 */
function SnapshotBannerShowcase() {
  const active = useSnapshotMode((s) => s.active);
  const enter = useSnapshotMode((s) => s.enterSnapshotMode);
  const exit = useSnapshotMode((s) => s.exitSnapshotMode);

  // Clean up the demo snapshot on unmount so leaving the showcase page
  // doesn't strand a fake banner on top of other routes. Only exit if
  // WE entered the showcase fixture (sentinel id) — a real route owner
  // could have entered for a non-fixture reason, and we shouldn't clobber
  // their state.
  React.useEffect(() => {
    return () => {
      if (useSnapshotMode.getState().active?.snapshot_id === 'showcase-fixture') {
        exit();
      }
    };
    // exit is stable from zustand; eslint may complain about deps but
    // including it would make this an on-every-render cleanup.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Section
      title="Snapshot banner"
      description="<SnapshotBanner /> — 32px ribbon fixed at the top of the viewport when useSnapshotMode().active is non-null. Mounted in __root.tsx. The buttons below flip the shared store so you can see the banner come and go."
    >
      <ShowcaseRow label="Toggle by kind">
        {SNAPSHOT_KIND_OPTIONS.map((opt) => (
          <button
            key={opt.kind}
            type="button"
            onClick={() =>
              enter({
                snapshot_id: 'showcase-fixture',
                captured_at: '2026-05-20T17:30:00.000Z',
                captured_by: 'Sarah Chen',
                kind: opt.kind,
              })
            }
            className="rounded-card border border-ink-200 bg-paper-0 px-3 py-1.5 text-body-sm text-ink-700 hover:bg-paper-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-amber-600"
          >
            {opt.label}
          </button>
        ))}
        <button
          type="button"
          onClick={exit}
          className="rounded-card border border-posture-critical bg-paper-0 px-3 py-1.5 text-body-sm text-posture-critical hover:bg-posture-critical-tint focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-amber-600"
        >
          Exit snapshot mode
        </button>
      </ShowcaseRow>

      <p className="text-meta text-ink-500">
        Active envelope:{' '}
        <code className="font-mono">{active ? JSON.stringify(active) : 'null'}</code>
      </p>
    </Section>
  );
}

function Section({
  title,
  description,
  children,
}: {
  title: string;
  description?: string;
  children: React.ReactNode;
}) {
  return (
    <section className="space-y-3 rounded-card border border-ink-100 bg-paper-0 p-6">
      <div>
        <h2 className="text-h2 font-semibold text-ink-900">{title}</h2>
        {description ? <p className="mt-1 text-body-sm text-ink-500">{description}</p> : null}
      </div>
      {children}
    </section>
  );
}

interface ShowcaseRowProps {
  label: string;
  children: React.ReactNode;
}

function ShowcaseRow({ label, children }: ShowcaseRowProps) {
  return (
    <div className="grid grid-cols-[120px_1fr] items-center gap-4 border-t border-ink-100 py-3 first:border-t-0 first:pt-0">
      <span className="text-meta">{label}</span>
      <div className="flex flex-wrap items-center gap-2">{children}</div>
    </div>
  );
}
